import type {ScannedResultVersionRow, ScanResult} from "../scan-model/ScanResult.tsx";
import {ProblematicDisplay} from "./ProblematicDisplay.tsx";
import type {VersionId} from "../scan-operations/2-ScanVersions.tsx";
import {EnhancedTaskAnalysisDisplay} from "./EnhancedTaskAnalysisDisplay.tsx";
import {ComponentList} from "./ComponentList.tsx";
import Styles from "./ScanResultDisplay.module.scss"
import {JsonDisplay} from "../../../components/json-display/JsonDisplay.tsx";
import {uploadReplacer} from "../../4-upload-results/uploadReplacer.ts";
import {useState} from "react";
import {Button, Icon} from "@matillion/component-library";
import {ScanDiagnosticInfo} from "../scan-display/ScanDiagnosticInfo.tsx";

function key(versionId: VersionId): string {
    return `${versionId.groupName}/${versionId.projectName}/${versionId.versionName}`
}

function VersionRow({version}: {version: ScannedResultVersionRow}) {
    return <div className={Styles.Section}>
        <h4>{key(version.versionId)}</h4>
        <div className={Styles.SectionContents}>
            <ProblematicDisplay problems={version.problemIndicators} />
            <ComponentList components={version.componentCount} />
        </div>
    </div>
}

interface ScanErrorWarningProps {
    errors: string[]
    fatalError?: unknown
}

export function ScanResultErrorWarning({errors, fatalError}: ScanErrorWarningProps) {
    if(errors.length === 0 && !fatalError) return null
    return <div style={{width: "100%", border: "1px solid red", padding: "0.5em"}}>
        <div style={{display: "flex", flexDirection: "row", alignItems: "baseline"}}>
            <Icon.Error />
            <p style={{marginLeft: "0.5em"}}>
                {fatalError
                    ? "An error occurred during scanning which prevented continuing. This scan is incomplete."
                    : "Warning: Errors occurred during scanning, this may have resulted in an incomplete scan"}
            </p>
        </div>
        <details>
            <summary style={{cursor: "pointer"}}>View diagnostic information</summary>
            <ScanDiagnosticInfo errors={errors} fatalError={fatalError} />
        </details>
    </div>
}

export function ScanResultDisplay({result}: { result: ScanResult}) {
    const [display, setDisplay] = useState(false)

    if(!display) {
        return <div>
            <ScanResultErrorWarning errors={result.scanErrors ?? []} />
            <p>Do you want to view your scan result?</p>
            <div style={{display: "flex", flexDirection: "row", alignItems: "baseline"}}>
                <Icon.Warning />
                <p style={{marginLeft: "0.5em"}}>This can potentially be quite large and cause your browser tab to slow down with large results, or on weak machines like remote desktops.</p>
            </div>
            <p style={{marginLeft: "0.5em"}}>If you are unsure, you can view the raw JSON results before uploading on the next page.</p>
            <Button onClick={()=>setDisplay(true)}>Open</Button>
        </div>
    }

    return <div className={Styles.ScanResultDisplay}>
        <ScanResultErrorWarning errors={result.scanErrors ?? []} />
        <div className={Styles.Section}>
            <h3>Overall</h3>
            <div className={Styles.SectionContents}>
                <ProblematicDisplay problems={result.jobScan.problemIndicators} drivers={result.driversScan}/>
                <ComponentList components={result.jobScan.componentCount} sharedJobs={result.sharedJobs.list}/>
            </div>
        </div>
        <div>
            <h3>Per Version</h3>
            {result.jobScan.byVersion.map(version => <VersionRow key={key(version.versionId)} version={version} />)}
        </div>
        <hr />
        {result.taskHistory && <EnhancedTaskAnalysisDisplay analysis={result.taskHistory} />}
        <hr />
        <div>
            <h3>Result JSON</h3>
            <JsonDisplay jsonObj={result} replacer={uploadReplacer} />
        </div>
    </div>
}